import type * as React from "react";
import { type FC, useRef, useState } from "react";
import { sendLoginRequest } from "~/features/auth/login/api/DoLogin";
import { doOnLoginAction } from "~/features/auth/login/loginUtils";
import { trackLoginAttempt, trackLoginSuccess } from "~/app/analytics/ga/ga";
import { Box, Button, Link, Stack, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { TextField } from "~/common/mui/TextField/TextField";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { STORAGE_KEY } from "~/features/compareProducts/components/ProductComparisonDrawerContainer";
import { Overlay } from "~/common/components/Overlay/Overlay";
import { getMediasVariant } from "~/features/navigation/components/TopBar/HeaderCircle";
import {
  ContentWrapper,
  FORM_CONTAINER_WIDTH,
  FormContainer,
  LeftSection,
  LogoRow,
  OverlayActionsSpacer,
  OverlayHeaderStyled,
  RightImage,
  RightTextContainer,
  StyledMediasLogo,
  StyledOverlayContent,
  StyledStack,
} from "./loginPopupStyles";
import ResetPasswordForm from "./ResetPasswordForm";
import { useResetPasswordLogic } from "~/features/auth/login/hooks/useResetPasswordLogic";
import ResetSuccessContent from "./ResetSuccessContent";
import { SCHAEFFLER_LOGO } from "~/utils/url/staticResources";

interface LoginContentProps {
  initialEmail: string;
  onResetClick: (email: string) => void;
  // use to configure an alternative eventAction in GA
  alternativeGAAction?: string;
  alternativeLoginAction?: () => void;
  onRegisterClick?: () => void;
  onFullWidthChange?: (fullWidth: boolean) => void;
}

export const LoginContent: FC<LoginContentProps> = ({
  initialEmail,
  alternativeGAAction,
  alternativeLoginAction,
  onRegisterClick,
  onFullWidthChange,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState(initialEmail);
  const { onResetSuccess } = useResetPasswordLogic();

  const successCb = (globalErrors: string[]) => {
    onResetSuccess(globalErrors);
    setShowSuccess(true);
    onFullWidthChange?.(true);
  };
  const mediasVariant = getMediasVariant();
  const { t } = useTranslation();

  const { login, error, errorComponent, inFlight } = useLoginApi({
    onBeforeLogin: () => trackLoginAttempt(alternativeGAAction),
    onLoginSuccess: () => trackLoginSuccess(alternativeGAAction),
    alternativeLoginAction,
  });

  const emailRef = useRef<HTMLInputElement>(null);
  const passwdRef = useRef<HTMLInputElement>(null);

  const invalidInputs = error === "login.error.credentials";

  const resetCb = () => {
    const email = emailRef.current?.value ?? "";

    setResetEmail(email);
    setShowResetPassword(true);
  };

  const doLogin = async () => {
    if (!emailRef.current || !passwdRef.current) {
      return;
    }
    await login(emailRef.current.value, passwdRef.current.value);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // noinspection JSIgnoredPromiseFromCall
    doLogin();
  };

  return (
    <>
      {!showResetPassword ? (
        <LeftSection>
          <Stack sx={{ alignItems: "center", gap: 4 }}>
            <Stack sx={{ alignItems: "center", gap: 2 }}>
              <LogoRow>
                <StyledMediasLogo variant={mediasVariant} href={false} />
                <Box
                  component="img"
                  sx={{ height: "12px", pl: 1 }}
                  src={SCHAEFFLER_LOGO}
                  alt={t("schaeffler.logo.alt.text")}
                />
              </LogoRow>

              <Typography variant="headlineLarge" sx={{ color: "textSecondary.main", textAlign: "center" }}>
                {t("sign.in.to.account")}
              </Typography>
            </Stack>

            <FormContainer>
              <form onSubmit={onSubmit} style={{ width: "100%" }}>
                <button hidden type="submit" />

                <StyledStack>
                  <TextField
                    inputRef={emailRef}
                    defaultValue={initialEmail}
                    name="j_username"
                    label={t("email.address.label")}
                    error={invalidInputs}
                    fullWidth
                  />

                  <TextField
                    inputRef={passwdRef}
                    name="j_password"
                    label={t("page.login.password")}
                    type="password"
                    error={invalidInputs}
                    fullWidth
                  />

                  {error && <Box sx={{ width: "100%" }}>{errorComponent}</Box>}

                  <Button color="primary" loading={inFlight} onClick={doLogin} fullWidth>
                    {t("header.link.signIn")}
                  </Button>

                  <Button variant="text" onClick={resetCb}>
                    {t("forgot.password")}
                  </Button>
                </StyledStack>
              </form>
            </FormContainer>

            <Stack sx={{ flexDirection: "row", gap: 1 }}>
              <Typography component={"span"} sx={{ color: "textSecondary.main" }}>
                {t("create.free.account")}{" "}
              </Typography>
              <Link component="button" onClick={onRegisterClick} sx={{ color: "inherit", fontWeight: "bold" }}>
                {t("sign.up")}
              </Link>
            </Stack>
          </Stack>
        </LeftSection>
      ) : (
        <LeftSection>
          {!showSuccess ? (
            <Stack sx={{ alignItems: "center", gap: 4, pb: 1 }}>
              <StyledStack sx={{ maxWidth: FORM_CONTAINER_WIDTH, textAlign: "center" }}>
                <Typography variant="headlineLarge" sx={{ color: "textSecondary.main" }}>
                  {t("reset.password.heading")}
                </Typography>

                <Typography variant="bodyLarge" sx={{ color: "textSecondary.main" }}>
                  {t("reset.password.subHeading")}
                </Typography>
              </StyledStack>

              <FormContainer>
                {!showSuccess && (
                  <>
                    <ResetPasswordForm initialEmail={resetEmail} onSuccess={successCb} disableEdit={false} />

                    <Button fullWidth type="submit" form="reset-password" onClick={() => setShowResetPassword(true)}>
                      {t("forgottenPwd.title")}
                    </Button>
                  </>
                )}

                <Button
                  variant="text"
                  onClick={() => {
                    setShowResetPassword(false);
                    setShowSuccess(false);
                    onFullWidthChange?.(false);
                  }}
                >
                  {t("back.to.login")}
                </Button>
              </FormContainer>
            </Stack>
          ) : (
            <ResetSuccessContent />
          )}
        </LeftSection>
      )}
    </>
  );
};

interface Props extends LoginContentProps {
  close: () => void;
}

const LoginPopup: FC<Props> = ({ close, ...contentProps }) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;
  const isTablet = screenSize <= useNumericScreenSize.tablet;
  const [fullWidthContent, setFullWidthContent] = useState(false);
  const onFullWidthChange = (fullWidth: boolean) => {
    setFullWidthContent(fullWidth);
    contentProps.onFullWidthChange?.(fullWidth);
  };

  return (
    <Overlay open={true}>
      <OverlayHeaderStyled onClose={close} />
      <StyledOverlayContent>
        <ContentWrapper singleColumn={fullWidthContent}>
          <LoginContent {...contentProps} onFullWidthChange={onFullWidthChange} />
          {!fullWidthContent && !isMobile && !isTablet && (
            <RightImage>
              <RightTextContainer>
                <Typography variant="headlineLarge" sx={{ color: "textPrimary" }}>
                  {t("configure.linear.solution.heading")}
                </Typography>
                <Typography variant="titleLarge" sx={{ color: "textSecondary" }}>
                  {t("configure.linear.solution.subHeading")}
                </Typography>
              </RightTextContainer>
            </RightImage>
          )}
        </ContentWrapper>
      </StyledOverlayContent>
      <OverlayActionsSpacer />
    </Overlay>
  );
};
export default LoginPopup;

const getTimezoneString = () => {
  const offsetInMinutes = new Date().getTimezoneOffset() * -1;
  return offsetInMinutes.toString();
};

interface LoginOptions {
  onBeforeLogin?: () => void;
  onLoginSuccess?: () => void;
  alternativeLoginAction?: () => void;
}
export function useLoginApi(options?: LoginOptions) {
  const [error, setError] = useState("");
  const [inFlight, setInFlight] = useState(false);
  const { t } = useTranslation();

  const onBeforeLogin = options?.onBeforeLogin ?? trackLoginAttempt;
  const onLoginSuccess = options?.onLoginSuccess ?? trackLoginSuccess;
  const alternativeLoginAction = options?.alternativeLoginAction;

  const doLogin = async (login: string, password: string) => {
    onBeforeLogin();

    try {
      setInFlight(true);
      setError("");
      const result = await sendLoginRequest({
        login,
        password,
        timezone: getTimezoneString(),
      });
      if (!result.error) {
        localStorage.setItem(STORAGE_KEY, "true");
        onLoginSuccess();
        if (alternativeLoginAction) {
          alternativeLoginAction();
        } else {
          doOnLoginAction(result.redirectUrl);
        }
        return true;
      } else {
        setInFlight(false);
        setError(t(result.error));
        return false;
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
      setInFlight(false);
      return false;
    }
  };

  let errorComponent = undefined;
  if (error) {
    errorComponent = <Alert severity="error">{error}</Alert>;
  }

  return {
    login: doLogin,
    error,
    inFlight,
    errorComponent,
  };
}
